import { Button } from '@shared/components/ui/button';
import { Search } from '@shared/components/ui/Search/Search';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/components/ui/select';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type StatusFilter = 'active' | 'inactive' | 'all';

interface RatesFiltersSectionProps {
  search: string;
  status: StatusFilter;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: StatusFilter) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
}

export const RatesFiltersSection = ({
  search,
  status,
  onSearchChange,
  onStatusChange,
  onClearFilters,
  hasActiveFilters,
}: RatesFiltersSectionProps) => {
  const { t } = useTranslation('ratesModule');
  const [localSearch, setLocalSearch] = useState(search);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setLocalSearch(newValue);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        onSearchChange(newValue.trim());
      }, 300);
    },
    [onSearchChange]
  );

  const handleSearchSubmit = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    onSearchChange(localSearch.trim());
  }, [localSearch, onSearchChange]);

  const handleStatusChange = useCallback(
    (value: string) => {
      onStatusChange(value as StatusFilter);
    },
    [onStatusChange]
  );

  const handleClearFilters = useCallback(() => {
    setLocalSearch('');
    onClearFilters();
  }, [onClearFilters]);

  return (
    <>
      <Search
        placeholder={t('filters.searchPlaceholder')}
        value={localSearch}
        onChange={handleSearchInputChange}
        onEnter={handleSearchSubmit}
        dataTestId="rates-search"
        inputDataTestId="rates-search-input"
      />

      <Select value={status} onValueChange={handleStatusChange}>
        <SelectTrigger className="w-40">
          <SelectValue placeholder={t('filters.status')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('filters.allStatuses')}</SelectItem>
          <SelectItem value="active">{t('status.active')}</SelectItem>
          <SelectItem value="inactive">{t('status.inactive')}</SelectItem>
        </SelectContent>
      </Select>

      {hasActiveFilters && (
        <Button variant="outline" size="sm" onClick={handleClearFilters}>
          {t('filters.clear')}
        </Button>
      )}
    </>
  );
};
